import type { Course } from '$lib/types';

export const courses: Course[] = [
	{
		id: 'python-basic',
		title: 'Python Basic',
		slug: 'python-basic',
		category: 'programming',
		level: 'foundation',
		description: 'Learn Python from scratch by actually building things.',
		longDescription: 'Python is a great first language. We\'ll take you from zero to writing real code, with plenty of hands-on practice along the way.',
		duration: '6 weeks',
		price: 8999,
		currency: 'INR',
		features: [
			'Variables, data types & operators',
			'Control flow & loops',
			'Functions & modules',
			'File handling',
			'Error handling',
			'3 practical projects'
		],
		targetAudience: ['Complete beginners', 'Students with no coding experience', 'Anyone who wants to learn programming'],
		prerequisites: [],
		highlighted: false,
		teachingModes: [
			{
				id: 'small-group',
				name: 'Small Group',
				description: 'Learn in a collaborative environment with 3-5 students. Interactive sessions with peer learning and group projects.'
			},
			{
				id: 'one-to-one',
				name: 'One to One',
				description: 'Personalized attention with dedicated 1-on-1 sessions. Tailored pace and curriculum based on your learning goals.'
			}
		],
		curriculum: [
			{
				id: 'pyb-mod-1',
				title: 'Getting Started with Python',
				lessons: [
					{ id: 'pyb-1-1', title: 'Installing Python & Setting Up Your Environment', duration: '30 min' },
					{ id: 'pyb-1-2', title: 'Your First Python Program', duration: '20 min' },
					{ id: 'pyb-1-3', title: 'Understanding the REPL & Scripts', duration: '25 min' }
				]
			},
			{
				id: 'pyb-mod-2',
				title: 'Variables & Data Types',
				lessons: [
					{ id: 'pyb-2-1', title: 'Variables and Naming Conventions', duration: '25 min' },
					{ id: 'pyb-2-2', title: 'Numbers and Basic Math', duration: '30 min' },
					{ id: 'pyb-2-3', title: 'Strings and String Operations', duration: '35 min' },
					{ id: 'pyb-2-4', title: 'Lists, Tuples, and Dictionaries', duration: '45 min' }
				]
			},
			{
				id: 'pyb-mod-3',
				title: 'Control Flow',
				lessons: [
					{ id: 'pyb-3-1', title: 'If Statements and Comparisons', duration: '30 min' },
					{ id: 'pyb-3-2', title: 'For Loops', duration: '35 min' },
					{ id: 'pyb-3-3', title: 'While Loops', duration: '25 min' },
					{ id: 'pyb-3-4', title: 'Break, Continue, and Pass', duration: '20 min' }
				]
			},
			{
				id: 'pyb-mod-4',
				title: 'Functions & Modules',
				lessons: [
					{ id: 'pyb-4-1', title: 'Defining Functions', duration: '35 min' },
					{ id: 'pyb-4-2', title: 'Parameters and Return Values', duration: '40 min' },
					{ id: 'pyb-4-3', title: 'Built-in Functions', duration: '30 min' },
					{ id: 'pyb-4-4', title: 'Creating and Using Modules', duration: '30 min' }
				]
			},
			{
				id: 'pyb-mod-5',
				title: 'Working with Files',
				lessons: [
					{ id: 'pyb-5-1', title: 'Reading Files', duration: '30 min' },
					{ id: 'pyb-5-2', title: 'Writing Files', duration: '30 min' },
					{ id: 'pyb-5-3', title: 'Working with CSV and JSON', duration: '40 min' }
				]
			},
			{
				id: 'pyb-mod-6',
				title: 'Projects',
				lessons: [
					{ id: 'pyb-6-1', title: 'Project 1: Number Guessing Game', duration: '1 hour' },
					{ id: 'pyb-6-2', title: 'Project 2: To-Do List CLI', duration: '1.5 hours' },
					{ id: 'pyb-6-3', title: 'Project 3: Simple Calculator', duration: '1 hour' }
				]
			}
		]
	},
	{
		id: 'python-advanced',
		title: 'Python Advanced',
		slug: 'python-advanced',
		category: 'programming',
		level: 'advanced',
		description: 'Take your Python skills further with OOP, APIs, databases, and real-world patterns.',
		longDescription: 'Ready to move beyond the basics? We\'ll cover OOP, building APIs, working with databases, and how pros actually structure Python code.',
		duration: '8 weeks',
		price: 12999,
		currency: 'INR',
		features: [
			'Object-Oriented Programming',
			'Working with APIs',
			'Database integration (SQL)',
			'Testing & debugging',
			'Code organization patterns',
			'Portfolio project'
		],
		targetAudience: ['Python Basic graduates', 'Developers who know some Python', 'Anyone who wants to write production-ready Python'],
		prerequisites: ['Python Basic or equivalent experience'],
		highlighted: false,
		teachingModes: [
			{
				id: 'small-group',
				name: 'Small Group',
				description: 'Learn in a collaborative environment with 3-5 students. Interactive sessions with peer learning and group projects.'
			},
			{
				id: 'one-to-one',
				name: 'One to One',
				description: 'Personalized attention with dedicated 1-on-1 sessions. Tailored pace and curriculum based on your learning goals.'
			}
		],
		curriculum: [
			{
				id: 'pya-mod-1',
				title: 'Object-Oriented Programming',
				lessons: [
					{ id: 'pya-1-1', title: 'Classes and Objects', duration: '45 min' },
					{ id: 'pya-1-2', title: 'Inheritance and Polymorphism', duration: '50 min' },
					{ id: 'pya-1-3', title: 'Encapsulation and Abstraction', duration: '40 min' },
					{ id: 'pya-1-4', title: 'Magic Methods', duration: '35 min' }
				]
			},
			{
				id: 'pya-mod-2',
				title: 'Working with APIs',
				lessons: [
					{ id: 'pya-2-1', title: 'REST API Fundamentals', duration: '40 min' },
					{ id: 'pya-2-2', title: 'Consuming APIs with requests', duration: '45 min' },
					{ id: 'pya-2-3', title: 'Building APIs with Flask', duration: '1 hour' },
					{ id: 'pya-2-4', title: 'Authentication & Error Handling', duration: '50 min' }
				]
			},
			{
				id: 'pya-mod-3',
				title: 'Database Integration',
				lessons: [
					{ id: 'pya-3-1', title: 'SQL Fundamentals', duration: '50 min' },
					{ id: 'pya-3-2', title: 'SQLite with Python', duration: '45 min' },
					{ id: 'pya-3-3', title: 'ORM with SQLAlchemy', duration: '1 hour' },
					{ id: 'pya-3-4', title: 'Database Design Patterns', duration: '45 min' }
				]
			},
			{
				id: 'pya-mod-4',
				title: 'Testing & Debugging',
				lessons: [
					{ id: 'pya-4-1', title: 'Unit Testing with pytest', duration: '50 min' },
					{ id: 'pya-4-2', title: 'Integration Testing', duration: '40 min' },
					{ id: 'pya-4-3', title: 'Debugging Techniques', duration: '35 min' },
					{ id: 'pya-4-4', title: 'Logging Best Practices', duration: '30 min' }
				]
			},
			{
				id: 'pya-mod-5',
				title: 'Code Organization',
				lessons: [
					{ id: 'pya-5-1', title: 'Project Structure', duration: '35 min' },
					{ id: 'pya-5-2', title: 'Virtual Environments', duration: '30 min' },
					{ id: 'pya-5-3', title: 'Package Management', duration: '35 min' },
					{ id: 'pya-5-4', title: 'Code Quality Tools', duration: '40 min' }
				]
			},
			{
				id: 'pya-mod-6',
				title: 'Portfolio Project',
				lessons: [
					{ id: 'pya-6-1', title: 'Project Planning', duration: '1 hour' },
					{ id: 'pya-6-2', title: 'Implementation Sprint', duration: '3 hours' },
					{ id: 'pya-6-3', title: 'Testing & Deployment', duration: '2 hours' }
				]
			}
		]
	},
	{
		id: 'html-css-js-basic',
		title: 'HTML/CSS/JS Basic',
		slug: 'html-css-js-basic',
		category: 'programming',
		level: 'foundation',
		description: 'Build websites from scratch using HTML, CSS, and JavaScript.',
		longDescription: 'Learn how the web actually works. We\'ll cover semantic HTML, responsive CSS layouts, and enough JavaScript to make things interactive.',
		duration: '6 weeks',
		price: 8999,
		currency: 'INR',
		features: [
			'Semantic HTML5',
			'Responsive CSS with Flexbox & Grid',
			'JavaScript fundamentals',
			'DOM manipulation',
			'Forms & validation',
			'2 website projects'
		],
		targetAudience: ['Beginners who want to build websites', 'Anyone interested in web development', 'Designers who want to code'],
		prerequisites: [],
		highlighted: false,
		teachingModes: [
			{
				id: 'small-group',
				name: 'Small Group',
				description: 'Learn in a collaborative environment with 3-5 students. Interactive sessions with peer learning and group projects.'
			},
			{
				id: 'one-to-one',
				name: 'One to One',
				description: 'Personalized attention with dedicated 1-on-1 sessions. Tailored pace and curriculum based on your learning goals.'
			}
		],
		curriculum: [
			{
				id: 'web-mod-1',
				title: 'HTML Fundamentals',
				lessons: [
					{ id: 'web-1-1', title: 'Document Structure & Semantic HTML', duration: '40 min' },
					{ id: 'web-1-2', title: 'Text Elements & Links', duration: '30 min' },
					{ id: 'web-1-3', title: 'Images, Audio, and Video', duration: '35 min' },
					{ id: 'web-1-4', title: 'Forms and Input Types', duration: '45 min' }
				]
			},
			{
				id: 'web-mod-2',
				title: 'CSS Styling',
				lessons: [
					{ id: 'web-2-1', title: 'Selectors and Specificity', duration: '35 min' },
					{ id: 'web-2-2', title: 'Box Model and Typography', duration: '40 min' },
					{ id: 'web-2-3', title: 'Colors and Backgrounds', duration: '30 min' },
					{ id: 'web-2-4', title: 'Flexbox Layout', duration: '50 min' },
					{ id: 'web-2-5', title: 'CSS Grid Layout', duration: '50 min' }
				]
			},
			{
				id: 'web-mod-3',
				title: 'Responsive Design',
				lessons: [
					{ id: 'web-3-1', title: 'Media Queries', duration: '40 min' },
					{ id: 'web-3-2', title: 'Mobile-First Approach', duration: '35 min' },
					{ id: 'web-3-3', title: 'Responsive Images', duration: '30 min' },
					{ id: 'web-3-4', title: 'Responsive Navigation', duration: '45 min' }
				]
			},
			{
				id: 'web-mod-4',
				title: 'JavaScript Fundamentals',
				lessons: [
					{ id: 'web-4-1', title: 'Variables and Data Types', duration: '35 min' },
					{ id: 'web-4-2', title: 'Functions and Scope', duration: '40 min' },
					{ id: 'web-4-3', title: 'DOM Selection and Manipulation', duration: '50 min' },
					{ id: 'web-4-4', title: 'Event Handling', duration: '45 min' }
				]
			},
			{
				id: 'web-mod-5',
				title: 'Forms & Validation',
				lessons: [
					{ id: 'web-5-1', title: 'Form Handling with JS', duration: '40 min' },
					{ id: 'web-5-2', title: 'Client-Side Validation', duration: '35 min' },
					{ id: 'web-5-3', title: 'Accessibility for Forms', duration: '30 min' }
				]
			},
			{
				id: 'web-mod-6',
				title: 'Projects',
				lessons: [
					{ id: 'web-6-1', title: 'Project 1: Personal Portfolio', duration: '3 hours' },
					{ id: 'web-6-2', title: 'Project 2: Landing Page', duration: '3 hours' }
				]
			}
		]
	},
	{
		id: 'web-gis-basic',
		title: 'Web GIS Programming',
		slug: 'web-gis-basic',
		category: 'programming',
		level: 'foundation',
		description: 'Build interactive maps and geospatial applications for the web with OpenLayers.',
		longDescription: 'Learn how to build interactive web maps and location-based applications for urban studies, architecture, planning, environment, and real-world spatial decision-making. This course introduces Web GIS from the ground up and guides students to create portfolio-ready mapping projects using open-source tools.',
		duration: '8 weeks',
		price: 11999,
		currency: 'INR',
		features: [
			'Web GIS fundamentals (projections, scale, zoom)',
			'OpenLayers deep dive',
			'Quick intro to Leaflet',
			'GeoJSON & spatial data formats',
			'2 classes/week',
			'Build one GIS project throughout'
		],
		targetAudience: ['Developers interested in mapping', 'GIS professionals wanting web skills', 'Anyone who wants to build location-based apps', 'Environmental/science professionals'],
		prerequisites: ['Basic HTML/CSS knowledge helpful', 'No prior GIS experience required'],
		highlighted: false,
		teachingModes: [
			{
				id: 'small-group',
				name: 'Small Group',
				description: 'Learn in a collaborative environment with 3-5 students. Interactive sessions with peer learning and group projects.'
			},
			{
				id: 'one-to-one',
				name: 'One to One',
				description: 'Personalized attention with dedicated 1-on-1 sessions. Tailored pace and curriculum based on your learning goals.'
			}
		],
		curriculum: [
			{
				id: 'gis-week-1',
				title: 'Week 1: Web GIS Fundamentals',
				lessons: [
					{ id: 'gis-1-1', title: 'What is GIS, Projections, and Coordinate Systems', duration: '1 hour' },
					{ id: 'gis-1-2', title: 'Scale, Web Map Tile System, and Environment Setup', duration: '1 hour' }
				]
			},
			{
				id: 'gis-week-2',
				title: 'Week 2: Quick Start with Leaflet & OpenLayers',
				lessons: [
					{ id: 'gis-2-1', title: 'Leaflet Quick Start and Basic Interactions', duration: '1 hour' },
					{ id: 'gis-2-2', title: 'OpenLayers Setup and Architecture', duration: '1 hour' }
				]
			},
			{
				id: 'gis-week-3',
				title: 'Week 3: OpenLayers Core & Projections',
				lessons: [
					{ id: 'gis-3-1', title: 'Working with Different Projections and Layers', duration: '1 hour' },
					{ id: 'gis-3-2', title: 'Controls, Interactions, and Markers', duration: '1 hour' }
				]
			},
			{
				id: 'gis-week-4',
				title: 'Week 4: Spatial Data and Formats',
				lessons: [
					{ id: 'gis-4-1', title: 'GeoJSON and Other Data Formats', duration: '1 hour' },
					{ id: 'gis-4-2', title: 'Loading, Styling, and Creating Choropleth Maps', duration: '1 hour' }
				]
			},
			{
				id: 'gis-week-5',
				title: 'Week 5: Advanced OpenLayers Features',
				lessons: [
					{ id: 'gis-5-1', title: 'Tile Layers and Drawing Features', duration: '1 hour' },
					{ id: 'gis-5-2', title: 'Layer Management and Interactive Controls', duration: '1 hour' }
				]
			},
			{
				id: 'gis-week-6',
				title: 'Week 6: APIs and Real-World Data',
				lessons: [
					{ id: 'gis-6-1', title: 'GIS APIs Overview and Integration', duration: '1 hour' },
					{ id: 'gis-6-2', title: 'Geocoding, Routing, and Real-time Data', duration: '1 hour' }
				]
			},
			{
				id: 'gis-week-7',
				title: 'Week 7: Building Your GIS Project',
				lessons: [
					{ id: 'gis-7-1', title: 'Project Planning and Architecture Setup', duration: '1 hour' },
					{ id: 'gis-7-2', title: 'Core Map Features and Data Integration', duration: '1 hour' }
				]
			},
			{
				id: 'gis-week-8',
				title: 'Week 8: Deployment & Portfolio',
				lessons: [
					{ id: 'gis-8-1', title: 'Advanced Features and User Interactions', duration: '1 hour' },
					{ id: 'gis-8-2', title: 'Testing, Deployment, and Portfolio Preparation', duration: '1 hour' }
				]
			}
		]
	},
	{
		id: 'golang-advanced',
		title: 'Golang Advanced',
		slug: 'golang-advanced',
		category: 'programming',
		level: 'advanced',
		description: 'Build fast, scalable backend systems with Go.',
		longDescription: 'Go is what companies use when they need serious performance. Learn the language from scratch, then build backend systems that can actually handle real traffic.',
		duration: '8 weeks',
		price: 14999,
		currency: 'INR',
		features: [
			'Go idioms & best practices',
			'Concurrency with goroutines & channels',
			'ReST API development',
			'Database operations',
			'Microservices patterns',
			'Production deployment'
		],
		targetAudience: ['Developers who already code', 'Backend developers', 'Anyone building systems that need to scale'],
		prerequisites: ['Experience with at least one programming language'],
		highlighted: false,
		teachingModes: [
			{
				id: 'small-group',
				name: 'Small Group',
				description: 'Learn in a collaborative environment with 3-5 students. Interactive sessions with peer learning and group projects.'
			},
			{
				id: 'one-to-one',
				name: 'One to One',
				description: 'Personalized attention with dedicated 1-on-1 sessions. Tailored pace and curriculum based on your learning goals.'
			}
		],
		curriculum: [
			{
				id: 'go-mod-1',
				title: 'Go Fundamentals',
				lessons: [
					{ id: 'go-1-1', title: 'Setup and Tooling', duration: '30 min' },
					{ id: 'go-1-2', title: 'Variables, Types, and Functions', duration: '45 min' },
					{ id: 'go-1-3', title: 'Packages and Imports', duration: '35 min' },
					{ id: 'go-1-4', title: 'Go idioms and Conventions', duration: '40 min' }
				]
			},
			{
				id: 'go-mod-2',
				title: 'Data Structures',
				lessons: [
					{ id: 'go-2-1', title: 'Arrays and Slices', duration: '40 min' },
					{ id: 'go-2-2', title: 'Maps and Structs', duration: '45 min' },
					{ id: 'go-2-3', title: 'Interfaces', duration: '50 min' },
					{ id: 'go-2-4', title: 'Error Handling Patterns', duration: '40 min' }
				]
			},
			{
				id: 'go-mod-3',
				title: 'Concurrency',
				lessons: [
					{ id: 'go-3-1', title: 'Goroutines', duration: '45 min' },
					{ id: 'go-3-2', title: 'Channels', duration: '50 min' },
					{ id: 'go-3-3', title: 'Select and Patterns', duration: '45 min' },
					{ id: 'go-3-4', title: 'sync Package', duration: '40 min' }
				]
			},
			{
				id: 'go-mod-4',
				title: 'Building APIs',
				lessons: [
					{ id: 'go-4-1', title: 'net/http Package', duration: '45 min' },
					{ id: 'go-4-2', title: 'Routing with chi or gin', duration: '40 min' },
					{ id: 'go-4-3', title: 'JSON Handling', duration: '35 min' },
					{ id: 'go-4-4', title: 'Middleware', duration: '40 min' }
				]
			},
			{
				id: 'go-mod-5',
				title: 'Database & Storage',
				lessons: [
					{ id: 'go-5-1', title: 'database/sql Package', duration: '45 min' },
					{ id: 'go-5-2', title: 'SQL Drivers and Patterns', duration: '40 min' },
					{ id: 'go-5-3', title: 'Redis Integration', duration: '35 min' }
				]
			},
			{
				id: 'go-mod-6',
				title: 'Production Deployment',
				lessons: [
					{ id: 'go-6-1', title: 'Docker Containerization', duration: '50 min' },
					{ id: 'go-6-2', title: 'CI/CD Pipelines', duration: '45 min' },
					{ id: 'go-6-3', title: 'Monitoring and Logging', duration: '40 min' }
				]
			}
		]
	},
	{
		id: 'agentic-ai-masterclass',
		title: 'Agentic AI Masterclass',
		slug: 'agentic-ai-masterclass',
		category: 'agentic-ai',
		level: 'advanced',
		description: 'Learn to code with AI as your partner, not just a tool.',
		longDescription: 'Stop throwing prompts at a wall and hoping for the best. Learn how to actually work with AI — from specs to architecture to shipping code that works.',
		duration: '10 weeks',
		price: 24000,
		currency: 'INR',
		features: [
			'Foundation Module (AI tools setup)',
			'BMAD agentic methodology',
			'Prompt engineering for coding',
			'AI-assisted debugging & refactoring',
			'Building with AI agents',
			'Build one real-world project throughout'
		],
		targetAudience: ['Developers who want to get better with AI', 'Experienced devs who want to move faster', 'Anyone who wants to stay relevant'],
		prerequisites: ['Basic programming knowledge', 'Familiarity with at least one language'],
		highlighted: true,
		teachingModes: [
			{
				id: 'small-group',
				name: 'Small Group',
				description: 'Learn in a collaborative environment with 3-5 students. Interactive sessions with peer learning and group projects.'
			},
			{
				id: 'one-to-one',
				name: 'One to One',
				description: 'Personalized attention with dedicated 1-on-1 sessions. Tailored pace and curriculum based on your learning goals.'
			}
		],
		curriculum: [
			{
				id: 'ai-week-1',
				title: 'Week 1: Foundation & Project Selection',
				lessons: [
					{ id: 'ai-1-1', title: 'Claude Code Setup', duration: '1 hour' },
					{ id: 'ai-1-2', title: 'Understanding AI & Prompting', duration: '1 hour' }
				]
			},
			{
				id: 'ai-week-2',
				title: 'Week 2: BMAD - Product Brief & Discovery',
				lessons: [
					{ id: 'ai-2-1', title: 'Product Brief & Discovery', duration: '1 hour' },
					{ id: 'ai-2-2', title: 'PRD and Requirements', duration: '1 hour' }
				]
			},
			{
				id: 'ai-week-3',
				title: 'Week 3: BMAD - Architecture & Planning',
				lessons: [
					{ id: 'ai-3-1', title: 'Architecture with AI', duration: '1 hour' },
					{ id: 'ai-3-2', title: 'Epic and Story Breakdown', duration: '1 hour' }
				]
			},
			{
				id: 'ai-week-4',
				title: 'Week 4: Prompt Engineering - Generation & Refactoring',
				lessons: [
					{ id: 'ai-4-1', title: 'Code Generation Patterns', duration: '1 hour' },
					{ id: 'ai-4-2', title: 'Refactoring with AI', duration: '1 hour' }
				]
			},
			{
				id: 'ai-week-5',
				title: 'Week 5: Prompt Engineering - Debugging & Testing',
				lessons: [
					{ id: 'ai-5-1', title: 'Debugging Strategies', duration: '1 hour' },
					{ id: 'ai-5-2', title: 'Testing with AI', duration: '1 hour' }
				]
			},
			{
				id: 'ai-week-6',
				title: 'Week 6: Spec-Driven Development',
				lessons: [
					{ id: 'ai-6-1', title: 'Writing AI-Ready Specs', duration: '1 hour' },
					{ id: 'ai-6-2', title: 'Story Files and Context', duration: '1 hour' }
				]
			},
			{
				id: 'ai-week-7',
				title: 'Week 7: Review & Starting Build',
				lessons: [
					{ id: 'ai-7-1', title: 'Review and Quality Gates', duration: '1 hour' },
					{ id: 'ai-7-2', title: 'Starting Your Project Build', duration: '1 hour' }
				]
			},
			{
				id: 'ai-week-8',
				title: 'Week 8: Building - Full-Stack Flow',
				lessons: [
					{ id: 'ai-8-1', title: 'Full-Stack Development Flow', duration: '1 hour' },
					{ id: 'ai-8-2', title: 'API Development', duration: '1 hour' }
				]
			},
			{
				id: 'ai-week-9',
				title: 'Week 9: Building - Database & Integration',
				lessons: [
					{ id: 'ai-9-1', title: 'Database Design', duration: '1 hour' },
					{ id: 'ai-9-2', title: 'Polishing & Testing Your App', duration: '1 hour' }
				]
			},
			{
				id: 'ai-week-10',
				title: 'Week 10: Deployment & Portfolio',
				lessons: [
					{ id: 'ai-10-1', title: 'Deployment & Production', duration: '1 hour' },
					{ id: 'ai-10-2', title: 'Portfolio & Next Steps', duration: '1 hour' }
				]
			}
		]
	}
];

export function getCourseBySlug(slug: string): Course | undefined {
	return courses.find(course => course.slug === slug);
}

export function getCoursesByCategory(category: Course['category']): Course[] {
	return courses.filter(course => course.category === category);
}

export function getCoursesByLevel(level: Course['level']): Course[] {
	return courses.filter(course => course.level === level);
}

export function getHighlightedCourses(): Course[] {
	return courses.filter(course => course.highlighted);
}

export function getOtherCourses(currentSlug: string, limit: number = 3): Course[] {
	return courses
		.filter(course => course.slug !== currentSlug)
		.slice(0, limit);
}
